import { useLocation } from "react-router-dom";
import {
  DESKTOP_CONTAINER,
  DESKTOP_CONTAINER_COMPACT,
  DESKTOP_CONTAINER_NARROW,
} from "./containers";

/**
 * Routes the desktop sidebar links to. It renders on all of them — navigation
 * chrome that appears on one destination but vanishes on the next is worse
 * than none at all.
 */
const SIDEBAR_ROUTES = new Set<string>([
  "/",
  "/search",
  "/my-courses",
  "/profile",
  "/profile/wishlist",
  "/profile/settings",
]);

/** Browsing a category is the search page with a filter pre-set. */
const CATEGORY_ROUTE = /^\/categories\/[^/]+$/;

/** A publisher's page: their header, then their courses. */
const MINIAPP_ROUTE = /^\/miniapp\/[^/]+$/;

/** An educator's page: the same shape as a publisher's — header, then courses. */
const EDUCATOR_ROUTE = /^\/educator\/[^/]+$/;

/**
 * The same, for routes carrying params. A page reached from the sidebar's own
 * destinations keeps the chrome rather than dropping the user into a bare page.
 */
const SIDEBAR_PATTERNS: RegExp[] = [CATEGORY_ROUTE, MINIAPP_ROUTE, EDUCATOR_ROUTE];

/**
 * Routes with a desktop layout, and the width rail each one uses. Listed here
 * means: drop the phone width cap.
 *
 * Deliberately narrower than SIDEBAR_ROUTES — a page can host the sidebar long
 * before its own content has been laid out for a wide viewport.
 */
const DESKTOP_LAYOUT_ROUTES = new Map<string, string>([
  ["/", DESKTOP_CONTAINER],
  ["/search", DESKTOP_CONTAINER_NARROW],
  ["/my-courses", DESKTOP_CONTAINER_NARROW],
  ["/profile", DESKTOP_CONTAINER_NARROW],
  ["/profile/wishlist", DESKTOP_CONTAINER_NARROW],
  ["/profile/edit", DESKTOP_CONTAINER_NARROW],
]);

/**
 * The same, for routes carrying params. Ordered, and only the first match
 * counts — a path never wants two rails.
 */
const DESKTOP_LAYOUT_PATTERNS: Array<[RegExp, string]> = [
  // Lesson player: stage + playlist side by side, so it wants the wide rail.
  [/^\/course\/[^/]+\/lesson\/[^/]+$/, DESKTOP_CONTAINER],
  // Course detail: content on one side, the buy rail on the other. No sidebar
  // for the same reason as the lesson page — the two columns want the width.
  [/^\/course\/[^/]+$/, DESKTOP_CONTAINER],
  // Checkout: the same split, choices beside the bill.
  [/^\/course\/[^/]+\/buy$/, DESKTOP_CONTAINER_NARROW],
  // Category browsing shares the search page's layout, so it shares its rail.
  [CATEGORY_ROUTE, DESKTOP_CONTAINER_NARROW],
  // Publisher page: a header plus a course grid — the same rail again.
  [MINIAPP_ROUTE, DESKTOP_CONTAINER_NARROW],
  // Educator page: header plus course grid, so it borrows the same rail.
  [EDUCATOR_ROUTE, DESKTOP_CONTAINER_NARROW],
  // Public certificate check, where the QR on a certificate lands: one card
  // of facts, often opened on a verifier's desktop. No sidebar — it sits
  // outside the platform.
  [/^\/certificate(\/[^/]+)?$/, DESKTOP_CONTAINER_COMPACT],
];

/**
 * Of those, the ones that also get the floating header and footer. The rest
 * carry their own TopBar and lean on the sidebar for navigation, so a second
 * bar across the top would only repeat what's already on screen.
 */
const HEADER_FOOTER_ROUTES = new Set<string>(["/"]);

export function useDesktopChrome() {
  const { pathname } = useLocation();
  // Tolerate a trailing slash ("/search/" === "/search"), but keep "/" itself.
  const normalized =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;

  // null means "not a desktop route" — keep the original phone width cap.
  const container =
    DESKTOP_LAYOUT_ROUTES.get(normalized) ??
    DESKTOP_LAYOUT_PATTERNS.find(([pattern]) => pattern.test(normalized))?.[1] ??
    null;

  return {
    hasSidebar:
      SIDEBAR_ROUTES.has(normalized) ||
      SIDEBAR_PATTERNS.some((pattern) => pattern.test(normalized)),
    container,
    hasHeaderFooter: HEADER_FOOTER_ROUTES.has(normalized),
  };
}
